import { AppInsights } from 'applicationinsights-js';

import { IAnalyticsService } from './types';

/**
 * Alerts Service
 */
export class AnalyticsService implements IAnalyticsService {
  /**
   * Initialize and start analytic service.
   */
  start(): void {
    // Download full ApplicationInsights script from CDN and initialize it with instrumentation key
    if (AppInsights.downloadAndSetup) {
      AppInsights.downloadAndSetup({ instrumentationKey: '89ae5e6a-9907-4676-92e2-b5181b4dc319' });
    }
  }

  /**
   * Track a page view (page display).
   * @param name - page name
   * @param url - page url
   * @param properties - Object with String properties (additionnal data, ex : { prop1: 'prop1', prop2: 'prop2' })
   * @param measurements - Object with number properties (metrics associated, ex : { measurement1: 1 })
   */
  trackPageView(
    name: string,
    url = '',
    properties: Record<string, string> = {},
    measurements: Record<string, number> = {}
  ): void {
    AppInsights.trackPageView(name, url, properties, measurements);
  }

  /**
   * Start tracking a page view (starts the timer, 'stopTrackPage' call is needed).
   * @param name - page name
   */
  startTrackPage(name: string): void {
    AppInsights.startTrackPage(name);
  }

  /**
   * Stop tracking a page view (Stops the timer that was started by calling 'startTrackPage')
   * @param name - page name
   * @param url - page url
   * @param properties - Object with String properties (additionnal data, ex : { prop1: 'prop1', prop2: 'prop2' })
   * @param measurements - Object with number properties (metrics associated, ex : { measurement1: 1 })
   */
  stopTrackPage(
    name: string,
    url: string,
    properties: Record<string, string>,
    measurements: Record<string, number>
  ): void {
    AppInsights.stopTrackPage(name, url, properties, measurements);
  }

  /**
   * Track an event.
   * @param name - Event name
   * @param properties - Object with String properties (additionnal data, ex : { prop1: 'prop1', prop2: 'prop2' })
   * @param measurements - Object with number properties (metrics associated, ex : { measurement1: 1 })
   */
  trackEvent(
    name: string,
    properties: Record<string, string> = {},
    measurements: Record<string, number> = {}
  ): void {
    AppInsights.trackEvent(name, properties, measurements);
  }

  /**
   * Track a metric positive numeric value.
   * @param name - Metric name
   * @param value - Metric value
   * @param properties - Object with String properties (additionnal data, ex : { prop1: 'prop1', prop2: 'prop2' })
   */
  trackMetric(name: string, value: number, properties: Record<string, string>): void {
    AppInsights.trackMetric(name, value, undefined, undefined, undefined, properties);
  }

  /**
   * Track an exception.
   * @param error - An Error from a catch clause
   * @param handleAt - Error handle at indicator (default 'unhandled')
   * @param properties - Object with String properties (additionnal data, ex : { prop1: 'prop1', prop2: 'prop2' })
   * @param measurements - Object with number properties (metrics associated, ex : { measurement1: 1 })
   */
  trackException(
    error: Error,
    handleAt = 'unhandled',
    properties: Record<string, string>,
    measurements: Record<string, number>
  ): void {
    AppInsights.trackException(error, handleAt, properties, measurements);
  }

  /**
   * Track a trace message.
   * @param message - Trace message
   * @param properties - Object with String properties (additionnal data, ex : { prop1: 'prop1', prop2: 'prop2' })
   */
  trackTrace(message: string, properties: Record<string, string>): void {
    AppInsights.trackTrace(message, properties);
  }
}
